import clsx from 'clsx';
import React from 'react';

const variantClasses = {
  primary:
    'bg-primary text-white border-[1.5px] border-transparent hover:bg-secondary',
  secondary:
    'bg-white text-primary border-[1.5px] border-primary hover:bg-cloud',
  tertiary:
    'bg-transparent text-primary border-[1.5px] border-transparent hover:bg-grey4',
  deleteHeavy:
    'bg-danger text-white border-[1.5px] border-transparent hover:bg-bittersweet',
  deleteLight:
    'bg-transparent text-danger border-[1.5px] border-transparent hover:bg-dangerLight',
} as const;

const disabledVariantClasses = {
  primary: 'disabled:border-grey2 disabled:bg-grey3 disabled:text-grey1',
  secondary: 'disabled:border-grey2 disabled:bg-grey3 disabled:text-grey1',
  tertiary: 'disabled:border-transparent disabled:bg-grey3 disabled:text-grey1',
  deleteHeavy: 'disabled:border-grey2 disabled:bg-grey3 disabled:text-grey1',
  deleteLight:
    'disabled:border-transparent disabled:bg-grey3 disabled:text-grey1',
} as const;

const loadingVariantClasses = {
  primary: 'hover:bg-primary opacity-70',
  secondary: 'hover:bg-white opacity-70',
  tertiary: 'hover:bg-transparent opacity-70',
  deleteHeavy: 'hover:bg-danger opacity-70',
  deleteLight: 'hover:bg-transparent opacity-70',
} as const;

const sizeClasses = {
  lg: 'py-2 px-3 gap-1 text-base uppercase',
  sm: 'py-1 px-[6px] gap-0.5 text-sm',
} as const;

export interface AppButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: keyof typeof variantClasses;
  size?: keyof typeof sizeClasses;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
}

const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'primary',
      size = 'lg',
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      className = '',
      disabled,
      type = 'button',
      children,
      ...buttonProps
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={clsx(
          'inline-flex items-center justify-center rounded-lg font-semibold transition-colors disabled:cursor-not-allowed active:opacity-70',
          sizeClasses[size],
          disabled && 'disabled:active:opacity-100',
          disabled && disabledVariantClasses[variant],
          loading && 'opacity-70',
          loading && loadingVariantClasses[variant],
          variantClasses[variant],
          fullWidth && 'w-full',
          className
        )}
        {...buttonProps}
      >
        {loading ? (
          <>
            <div className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-current border-t-transparent" />
            {children}
          </>
        ) : (
          <>
            {leftIcon && (
              <span className="shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px]">
                {leftIcon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span className="shrink-0 [&>svg]:w-5 [&>svg]:h-5">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </button>
    );
  }
);

AppButton.displayName = 'AppButton';

export default AppButton;
